import { defineStore } from "pinia";
import { useCookieManager } from "./composables/useCookieManager";
import { useCheckMismatch } from "./composables/useCheckMismatch";
import { useUserStore, PO_OVERRIDE_COOKIE_NAME } from "~/stores/user";
import {
  seedFavourites,
  resetFavouritesCache,
  setFavouritesLoadPromise,
} from "~/composables/favourite/useFavourites";
import { FEATURES } from "~/config/pages/features.config";
import MY_FAVOURITES from "~/graphql/myFavourites.graphql";

/**
 * Store that can work both client and server side.
 */
export const useAuthStore = defineStore(
  "useAuthStore",
  {
    state: () => ({
      userToken: null,
      adminToken: null,
    }),
    getters: {
      isAuthenticated: (state) => !!state.userToken,
    },
    actions: {
      setToken(token) {
        this.userToken = token;

        useCookieManager(this.$nuxt).setTokenCookie(token, "user");
      },
      setAdminToken(token) {
        this.adminToken = token;
        useCookieManager(this.$nuxt).setTokenCookie(token, "admin");
      },
      logout() {
        useCookieManager(this.$nuxt).clearTokenCookieForAll();
        this.userToken = null;
        this.adminToken = null;
        this.$nuxt.$cookie.remove(PO_OVERRIDE_COOKIE_NAME, {
          domain: this.$nuxt.$helpers.getMainDomain(),
          path: "/",
        });
        useUserStore().$reset();
        resetFavouritesCache();

        // TODO find a better place for this
        this.$nuxt.$sentry.setUser(null);
      },
      loadFavourites() {
        const favouritesEnabled = this.$nuxt.$branding?.hasFeature(
          FEATURES.FAVOURITES
        );
        if (!process.client || !favouritesEnabled || !this.isAuthenticated) {
          return;
        }
        // Best-effort, fire-and-forget; never block auth on favourites. The
        // promise is registered so the favourite auth dialog can await this
        // load before applying a pending favourite (avoids a stale seed
        // reverting it).
        const promise = this.$nuxt
          .$query(MY_FAVOURITES, { entityType: null })
          .then(({ myFavourites }) => seedFavourites(myFavourites))
          .catch((error) => {
            this.$nuxt.$logger.warn("Failed to load favourites", error);
          });
        return setFavouritesLoadPromise(promise);
      },
      async loginWithToken(token, skipAuthErrorPages = false) {
        this.setToken(token);
        const userStore = useUserStore();

        await userStore.queryMe(skipAuthErrorPages);

        try {
          this.$nuxt.$sentry.setUser({
            id: userStore.me.id,
            email: userStore.me.email,
          });

          if (process.client) {
            this.$nuxt.$trackers.identifyUser({
              id: userStore.me.id,
              name: userStore.me.name,
              email: userStore.me.email,
            });
          }
        } catch (error) {
          console.error("Error when settings user for third party apps", error);
        }
        // TODO find a better place for this

        useCheckMismatch(this.$nuxt)(userStore.me.id, this.userToken);

        if (userStore.me.isAdmin) {
          this.setAdminToken(this.userToken);
        }

        this.loadFavourites();

        useCookieManager(this.$nuxt).clearCampaignsCookie();
      },
    },
  },
  {}
);
